import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/context/AuthContext";
import AuthCallback from "@/components/AuthCallback";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import LandingDoors from "@/pages/LandingDoors";
import Home from "@/pages/Home";
import Services from "@/pages/Services";
import Gallery from "@/pages/Gallery";
import Booking from "@/pages/Booking";
import BookingSuccess from "@/pages/BookingSuccess";
import PaymentCallback from "@/pages/PaymentCallback";
import Contact from "@/pages/Contact";
import AdminDashboard from "@/pages/AdminDashboard";
import MyBookings from "@/pages/MyBookings";
import RepairQuote from "@/pages/RepairQuote";
import FixRoom from "@/pages/FixRoom";
import RestoreRoom from "@/pages/RestoreRoom";
import DetailRoom from "@/pages/DetailRoom";
import FleetServices from "@/pages/FleetServices";
import WorkshopLogin from "@/pages/WorkshopLogin";
import WorkshopDashboard from "@/pages/WorkshopDashboard";
import TrackService from "@/pages/TrackService";
import Login from "@/pages/Login";
import Signup from "@/pages/Signup";
import VerifyEmail from "@/pages/VerifyEmail";
import { 
  TermsAndConditions, 
  RefundPolicy, 
  PrivacyPolicy, 
  ServiceFulfilmentPolicy, 
  PaymentInformation, 
  ContactUsPage 
} from "@/pages/PolicyPages";

// Routes that should show the full-screen room experience (no navbar/footer)
const roomRoutes = ['/', '/fix', '/restore', '/detail'];

// Workshop portal routes (completely separate, no navbar/footer)
const workshopRoutes = ['/staff', '/staff/login'];

// Auth routes (no navbar/footer, clean experience)
const authRoutes = ['/login', '/signup', '/verify'];

// Policy pages (have their own navbar embedded)
const policyRoutes = ['/terms', '/refund-policy', '/privacy-policy', '/service-policy', '/payment-info', '/contact-us'];

function AppRouter() {
  const location = useLocation();
  
  // CRITICAL: Check URL fragment for session_id synchronously during render
  // This prevents race conditions by processing new session_id FIRST
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  const isRoomRoute = roomRoutes.includes(location.pathname);
  const isWorkshopRoute = location.pathname.startsWith('/staff');
  const isAuthRoute = authRoutes.includes(location.pathname);
  
  // Workshop portal - completely separate experience
  if (isWorkshopRoute) {
    return (
      <Routes>
        <Route path="/staff/login" element={<WorkshopLogin />} />
        <Route path="/staff" element={<WorkshopDashboard />} />
      </Routes>
    );
  }
  
  // Auth routes - clean experience without navbar/footer
  if (isAuthRoute) {
    return (
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<Signup />} />
        <Route path="/verify" element={<VerifyEmail />} />
      </Routes>
    );
  }
  
  // Policy pages - have their own layout with navbar
  if (policyRoutes.includes(location.pathname)) {
    return (
      <Routes>
        <Route path="/terms" element={<TermsAndConditions />} />
        <Route path="/refund-policy" element={<RefundPolicy />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/service-policy" element={<ServiceFulfilmentPolicy />} />
        <Route path="/payment-info" element={<PaymentInformation />} />
        <Route path="/contact-us" element={<ContactUsPage />} />
      </Routes>
    );
  }
  
  // Room routes get full-screen experience without navbar/footer
  if (isRoomRoute) {
    return (
      <Routes>
        <Route path="/" element={<LandingDoors />} />
        <Route path="/fix" element={<FixRoom />} />
        <Route path="/restore" element={<RestoreRoom />} />
        <Route path="/detail" element={<DetailRoom />} />
      </Routes>
    );
  }
  
  // All other routes have navbar/footer
  return (
    <>
      <Navbar />
      <Routes>
        <Route path="/home" element={<Home />} />
        <Route path="/services" element={<Services />} />
        <Route path="/gallery" element={<Gallery />} />
        <Route path="/booking" element={<Booking />} />
        <Route path="/booking/success" element={<BookingSuccess />} />
        <Route path="/payment/callback" element={<PaymentCallback />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/admin" element={<AdminDashboard />} />
        <Route path="/my-bookings" element={<MyBookings />} />
        <Route path="/repair-quote" element={<RepairQuote />} />
        <Route path="/fleet-services" element={<FleetServices />} />
        <Route path="/track" element={<TrackService />} />
        <Route path="/track/:reference" element={<TrackService />} />
        {/* Legacy service routes */}
        <Route path="/mechanical" element={<Services />} />
        <Route path="/panel-beating" element={<Services />} />
        <Route path="/detailing" element={<Services />} />
        <Route path="/paint-correction" element={<Services />} />
        <Route path="/ceramic-coating" element={<Services />} />
      </Routes>
      <Footer />
    </>
  );
}

function App() {
  return (
    <div className="App min-h-screen bg-[#0B0B0B]">
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
        </AuthProvider>
      </BrowserRouter>
      <Toaster 
        position="top-right"
        toastOptions={{
          style: {
            background: '#1F1F1F',
            border: '1px solid rgba(192, 192, 192, 0.1)',
            color: '#F5F5F5',
          },
        }}
      />
    </div>
  );
}

export default App;
